'use client';
import { jsxs, jsx, Fragment } from '@emotion/react/jsx-runtime';
import { useState } from 'react';
import { Transition } from '@s7/base';
import { useDisclosure, useEnforceFocus, useUncontrolled } from '@s7/hooks';
import { secretlyPrevent, composeHandlers } from '@s7/react-utils';
import { getDefaultZIndex } from '@s7/styles';
import { useInputProps, Popover, Input, Loader } from '@s7/ui';
import { defaultProps } from './FooterSearch.contract.js';
import useStyles from './FooterSearch.styles.js';
import { getInputAttributes } from './getInputAttributes.js';
import { getListWrapperAttributes } from './getListWrapperAttributes.js';
import { useIMESessionGuard } from './useIMESessionGuard.js';
import { InputResetItem } from './slots/InputResetItem/InputResetItem.js';
import { SearchLayout } from './layouts/SearchLayout/SearchLayout.js';
import { openResource } from '../../../common/ResourceLink/ResourceLink.js';
import { countItems } from './utils/countItems.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
var FooterSearch = function(props) {
    var _useInputProps = useInputProps("FooterSearch", defaultProps, props), defaultOpened = _useInputProps.defaultOpened, navigation = _useInputProps.navigation, helpCenter = _useInputProps.helpCenter, loading = _useInputProps.loading, showMore = _useInputProps.showMore, fallback = _useInputProps.fallback, onShowMoreClick = _useInputProps.onShowMoreClick, onNavigationItemClick = _useInputProps.onNavigationItemClick, onItemClick = _useInputProps.onItemClick, onShowMoreSelect = _useInputProps.onShowMoreSelect, onNavigationItemSelect = _useInputProps.onNavigationItemSelect, onItemSelect = _useInputProps.onItemSelect, value = _useInputProps.value, defaultValue = _useInputProps.defaultValue, onChange = _useInputProps.onChange, onInputReset = _useInputProps.onInputReset, inputProps = _useInputProps.inputProps, wrapperProps = _useInputProps.wrapperProps; _useInputProps.styles; _useInputProps.classNames; var others = _object_without_properties_loose(_useInputProps, [
        "defaultOpened",
        "navigation",
        "helpCenter",
        "loading",
        "showMore",
        "fallback",
        "onShowMoreClick",
        "onNavigationItemClick",
        "onItemClick",
        "onShowMoreSelect",
        "onNavigationItemSelect",
        "onItemSelect",
        "value",
        "defaultValue",
        "onChange",
        "onInputReset",
        "inputProps",
        "wrapperProps",
        "styles",
        "classNames"
    ]);
    var _useState = useState(-1), hovered = _useState[0], setHovered = _useState[1];
    var _useState1 = useState("bottom"), position = _useState1[0], setPosition = _useState1[1];
    var _useDisclosure = useDisclosure(defaultOpened), dropdownOpened = _useDisclosure[0], _useDisclosure_ = _useDisclosure[1], openDropdown = _useDisclosure_.open, closeDropdown = _useDisclosure_.close;
    var _useIMESessionGuard = useIMESessionGuard(), imeSafeCallback = _useIMESessionGuard[0], startIMESession = _useIMESessionGuard[1], endIMESession = _useIMESessionGuard[2];
    var _useEnforceFocus = useEnforceFocus(), inputRef = _useEnforceFocus[0], enforceInputFocus = _useEnforceFocus[1];
    var _useUncontrolled = useUncontrolled({
        value: value,
        defaultValue: defaultValue,
        finalValue: "",
        onChange: onChange
    }), inputValue = _useUncontrolled[0], handleInputValueChange = _useUncontrolled[1];
    var isInputDirty = inputValue.length > 0;
    var totalItems = countItems(navigation == null ? void 0 : navigation.items, helpCenter == null ? void 0 : helpCenter.items, showMore);
    var handleInputKeydown = imeSafeCallback(function(event) {
        switch(event.key){
            case "ArrowUp":
                {
                    event.preventDefault();
                    if (totalItems) {
                        setHovered(function(current) {
                            return current > 0 ? current - 1 : totalItems - 1;
                        });
                    }
                    break;
                }
            case "ArrowDown":
                {
                    event.preventDefault();
                    if (totalItems) {
                        setHovered(function(current) {
                            return current < totalItems - 1 ? current + 1 : 0;
                        });
                    }
                    break;
                }
            case "Enter":
                {
                    secretlyPrevent(event);
                    openDropdown();
                    var isShowMoreSelected = hovered + 1 === totalItems;
                    if (isShowMoreSelected) {
                        composeHandlers(onShowMoreSelect, function() {
                            return (showMore == null ? void 0 : showMore.link) && openResource(showMore.link);
                        })(event);
                        break;
                    }
                    if (navigation == null ? void 0 : navigation.items) {
                        var item = navigation.items[hovered];
                        if (item) {
                            composeHandlers(onNavigationItemSelect, function() {
                                return (item == null ? void 0 : item.link) && openResource(item.link);
                            }, {
                                pickEventFromArgs: function() {
                                    for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
                                        args[_key] = arguments[_key];
                                    }
                                    return args[1];
                                }
                            })(item, event, hovered);
                            break;
                        }
                    }
                    if (helpCenter == null ? void 0 : helpCenter.items) {
                        var _navigation_items;
                        var itemIndex = (navigation == null ? void 0 : (_navigation_items = navigation.items) == null ? void 0 : _navigation_items.length) ? hovered - navigation.items.length : hovered;
                        var item1 = helpCenter == null ? void 0 : helpCenter.items[itemIndex];
                        if (item1) {
                            composeHandlers(onItemSelect, function() {
                                return (item1 == null ? void 0 : item1.link) && openResource(item1.link);
                            }, {
                                pickEventFromArgs: function() {
                                    for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
                                        args[_key] = arguments[_key];
                                    }
                                    return args[1];
                                }
                            })(item1, event, hovered);
                            break;
                        }
                    }
                    break;
                }
            case "Escape":
                {
                    if (dropdownOpened) {
                        event.preventDefault();
                        closeDropdown();
                    }
                }
        }
    });
    var handleInputReset = function(event) {
        event.preventDefault();
        handleInputValueChange("");
        onInputReset == null ? void 0 : onInputReset();
        enforceInputFocus();
    };
    var handleInputResetKeydown = function(event) {
        if (event.key === "Escape") {
            event.preventDefault();
            enforceInputFocus();
            onInputReset == null ? void 0 : onInputReset();
        }
    };
    var listWrapperAttributes = getListWrapperAttributes(inputProps.id);
    var inputAttributes = getInputAttributes(inputProps.id, hovered, dropdownOpened);
    var zIndex = getDefaultZIndex("modal");
    var _useStyles = useStyles(), classes = _useStyles[0];
    return /*#__PURE__*/ jsxs(Popover, _extends({}, others, {
        middlewares: {
            flip: true,
            shift: false
        },
        offset: -68,
        opened: dropdownOpened,
        position: "bottom",
        shadow: "z500w",
        transitionProps: {
            transition: "fade",
            duration: 0
        },
        width: "target",
        withRoles: false,
        zIndex: zIndex - 11,
        onPositionChange: setPosition,
        children: [
            /*#__PURE__*/ jsx(Popover.Target, {
                children: /*#__PURE__*/ jsx(Input.Root, _extends({}, wrapperProps, {
                    classNames: {
                        root: classes.root
                    },
                    children: /*#__PURE__*/ jsx(Input, _extends({
                        ref: inputRef,
                        autoComplete: "off",
                        type: "search",
                        withBorder: false
                    }, others, dropdownOpened && {
                        sx: {
                            zIndex: zIndex - 10
                        }
                    }, {
                        classNames: {
                            input: classes.input,
                            wrapper: classes.wrapper
                        },
                        rightSide: isInputDirty && /*#__PURE__*/ jsxs(Fragment, {
                            children: [
                                /*#__PURE__*/ jsx(Transition, {
                                    mounted: loading || false,
                                    transition: "fade",
                                    children: function(style) {
                                        return /*#__PURE__*/ jsx(Loader, {
                                            color: "primary.mid",
                                            size: "sm1",
                                            style: style
                                        });
                                    }
                                }),
                                /*#__PURE__*/ jsx(InputResetItem, {
                                    onClick: handleInputReset,
                                    onKeyDown: handleInputResetKeydown
                                })
                            ]
                        }),
                        value: inputValue
                    }, inputAttributes, {
                        onChange: function(event) {
                            handleInputValueChange(event.currentTarget.value);
                            openDropdown();
                        },
                        onBlur: closeDropdown,
                        onClick: openDropdown,
                        onCompositionEnd: endIMESession,
                        onCompositionStart: startIMESession,
                        onFocus: openDropdown,
                        onKeyDown: handleInputKeydown
                    }))
                }))
            }),
            /*#__PURE__*/ jsx(Popover.Dropdown, _extends({}, listWrapperAttributes, {
                classNames: {
                    dropdown: classes.dropdown
                },
                onMouseDown: function(event) {
                    return event.preventDefault();
                },
                children: /*#__PURE__*/ jsx(SearchLayout, {
                    "data-position-bottom": position === "bottom",
                    fallback: fallback,
                    helpCenter: helpCenter,
                    highlight: inputValue,
                    hovered: hovered,
                    loading: loading,
                    navigation: navigation,
                    showMore: showMore,
                    totalItems: totalItems,
                    onItemClick: onItemClick,
                    onItemHover: setHovered,
                    onNavigationItemClick: onNavigationItemClick,
                    onShowMoreClick: onShowMoreClick
                })
            }))
        ]
    }));
};

export { FooterSearch };
